import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { FieldTeamVisibility } from "@mapmycustomers/shared/enum/FieldVisibility";
import Role from "@mapmycustomers/shared/types/Role";
import FieldTeamVisibilityRecord from "@mapmycustomers/shared/types/schema/FieldTeamVisibilityRecord";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";
import Team from "@mapmycustomers/shared/types/Team";
import { SearchField } from "@mapmycustomers/ui";

import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useLowerCaseState from "@app/util/hook/useLowerCaseState";
import { buildFieldVisibilityRecordForTeamRole } from "@app/util/schema/fieldVisibilityRecord";

import messages from "./messages";
import TeamItem from "./TeamItem";
import styles from "./TeamVisibility.module.scss";

interface Props {
  calculatedField?: boolean;
  disabledFieldVisibilities?: FieldTeamVisibility[];
  onChange?: (value: FieldTeamVisibilityRecord[]) => void;
  phiEnabled?: boolean;
  schemaField?: SchemaField;
  teams: Team[];
  value?: FieldTeamVisibilityRecord[];
  visible: boolean;
}

const TeamVisibility: React.FC<Props> = ({
  calculatedField,
  disabledFieldVisibilities,
  onChange,
  phiEnabled,
  schemaField,
  teams,
  value,
  visible,
}) => {
  const intl = useIntl();
  const [query, setQuery] = useLowerCaseState("");

  const handleChange = useCallback(
    (team: null | Team, roleMap: Record<Role["id"], FieldTeamVisibility>) => {
      let updatedValue = (value ?? []).filter((record) =>
        team === null ? record.team !== null : record.team?.id !== team.id
      );
      updatedValue = [
        ...updatedValue,
        ...Object.entries(roleMap).map(([roleIdString, visibility]) =>
          buildFieldVisibilityRecordForTeamRole(team, parseInt(roleIdString, 10), visibility)
        ),
      ];
      onChange?.(updatedValue);
    },
    [onChange, value]
  );

  const roleMapByTeam = useMemo(() => {
    const map = new Map<null | Team["id"], Record<Role["id"], FieldTeamVisibility>>();
    value?.forEach((record) => {
      const key = record.team?.id ?? null;
      if (!map.has(key)) {
        map.set(key, {});
      }
      map.get(key)![record.role.id] = record.visibility;
    });
    return map;
  }, [value]);

  const showNoTeam = !query || intl.formatMessage(messages.noTeam).toLowerCase().includes(query);
  const filteredTeams = useMemo(
    () => (query ? teams.filter(({ name }) => name.toLowerCase().includes(query)) : teams),
    [query, teams]
  );

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.container}>
      <SearchField
        allowClear
        className={styles.search}
        onChange={setQuery}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        size="large"
      />
      <div className={styles.container}>
        {showNoTeam && (
          <TeamItem
            calculatedField={calculatedField}
            disabledFieldVisibilities={disabledFieldVisibilities}
            key="noTeam"
            onChange={handleChange}
            phiEnabled={phiEnabled}
            schemaField={schemaField}
            team={null}
            value={roleMapByTeam.get(null)}
          />
        )}
        {filteredTeams.map((team) => (
          <TeamItem
            calculatedField={calculatedField}
            disabledFieldVisibilities={disabledFieldVisibilities}
            key={team.id}
            onChange={handleChange}
            phiEnabled={phiEnabled}
            schemaField={schemaField}
            team={team}
            value={roleMapByTeam.get(team.id)}
          />
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
});

export default connect(mapStateToProps)(TeamVisibility);
